import { createAsyncThunk } from "@reduxjs/toolkit";
import { environment } from "../../environments";
import {
  DashboardAdsLinkRequest,
  DashboardAdsLinkResponse,
  GetKPIsRequest,
  GetKPIsResponse,
  GetKPIMapRequest,
  GetKPIMapResponse,
  SetKPIIndexRequest,
  SetKPIIndexResponse,
  GetCounterRequest,
  GetCounterResponse,
  GetCancelStatRequest,
  GetCancelStatResponse,
  GetFutureMonetaryValueRequest,
  GetFutureMonetaryValueResponse,
  LastActivitiesRequest,
  LastActivitiesResponse,
  GetProjectUnactivityRequest,
  GetProjectUnactivityResponse,
  GetSalesStatisticRequest,
  GetSalesStatisticResponse,
  GetSalesCounterRequest,
  GetSalesCounterResponse,
  GetLeadSourceStatRequest,
  GetLeadSourceStatResponse,
  GetPhoneCallSourceStatRequest,
  GetPhoneCallSourceStatResponse,
  LeadsWithoutActivityPerDaysRequest,
  LeadsWithoutActivityPerDaysResponse,
} from "../../models/Dashboard.model";
const API_BASE_URL = environment.apiUrl;

export const getKPIMap = createAsyncThunk<
  GetKPIMapResponse,
  GetKPIMapRequest,
  { rejectValue: string }
>("dashboard/getKPIMap", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Dashboard/GetKPIMap`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data: GetKPIMapResponse = await response.json();
    if (!data.success) {
      return rejectWithValue(data.message || "Failed to get KPI map");
    }

    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const getKPIs = createAsyncThunk<
  GetKPIsResponse,
  GetKPIsRequest,
  { rejectValue: string }
>("dashboard/getKPIs", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Dashboard/GetKPIs`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const rawData = await response.json();
    const data: GetKPIsResponse = {
      ...rawData,
      kPIs: rawData.kPIs || rawData.KPIs || rawData.kpis || [],
    };

    if (!data.success) {
      return rejectWithValue(data.message || "Failed to get KPIs");
    }

    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const getDashboardAdsLink = createAsyncThunk<
  DashboardAdsLinkResponse,
  DashboardAdsLinkRequest,
  { rejectValue: string }
>("dashboard/getDashboardAdsLink", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}Dashboard/GetDashboardAdsLink`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestData),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const rawData = await response.json();
    const data: DashboardAdsLinkResponse = {
      ...rawData,
      adsLinks: rawData.adsLinks || rawData.AdsLinks,
    };
    if (!data.success) {
      return rejectWithValue(
        data.message || "Failed to get dashboard ads link",
      );
    }

    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const setKPIIndex = createAsyncThunk<
  SetKPIIndexResponse,
  SetKPIIndexRequest,
  { rejectValue: string }
>("dashboard/setKPIIndex", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Dashboard/SetKPIIndex`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data: SetKPIIndexResponse = await response.json();
    if (!data.success) {
      return rejectWithValue(data.message || "Failed to set KPI index");
    }

    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const getCounter = createAsyncThunk<
  GetCounterResponse,
  GetCounterRequest,
  { rejectValue: string }
>("dashboard/getCounter", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Dashboard/GetCounter`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const data: GetCounterResponse = await response.json();
    if (!data.success) {
      return rejectWithValue(data.message || "Failed to get counter");
    }

    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});
export const getFutureMonetaryValue = createAsyncThunk<
  GetFutureMonetaryValueResponse,
  GetFutureMonetaryValueRequest,
  { rejectValue: string }
>(
  "dashboard/getFutureMonetaryValue",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}Dashboard/GetFutureMonetaryValue`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data: GetFutureMonetaryValueResponse = await response.json();
      if (!data.success) {
        return rejectWithValue(
          data.message || "Failed to get future monetary value",
        );
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

export const getCancelStat = createAsyncThunk<
  GetCancelStatResponse,
  GetCancelStatRequest,
  { rejectValue: string }
>("dashboard/getCancelStat", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Dashboard/GetCancelStat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const rawData = await response.json();
    const data: GetCancelStatResponse = {
      ...rawData,
      cancelStat: rawData.cancelStat || rawData.CancelStat,
    };

    if (!data.success) {
      return rejectWithValue(data.message || "Failed to get cancel stat");
    }

    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const getLastActivities = createAsyncThunk<
  LastActivitiesResponse,
  LastActivitiesRequest,
  { rejectValue: string }
>("dashboard/getLastActivities", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Dashboard/GetLastActivities`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const rawData = await response.json();
    const data: LastActivitiesResponse = {
      ...rawData,
      lastActivities: rawData.lastActivities || rawData.LastActivities,
    };

    if (!data.success) {
      return rejectWithValue(data.message || "Failed to get last activities");
    }

    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const getProjectUnactivity = createAsyncThunk<
  GetProjectUnactivityResponse,
  GetProjectUnactivityRequest,
  { rejectValue: string }
>(
  "dashboard/getProjectUnactivity",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}Dashboard/GetProjectUnactivity`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const rawData = await response.json();
      const data: GetProjectUnactivityResponse = {
        ...rawData,
        projectUnactivity:
          rawData.projectUnactivity || rawData.ProjectUnactivity,
      };

      if (!data.success) {
        return rejectWithValue(
          data.message || "Failed to get project unactivity",
        );
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

export const getSalesStatistic = createAsyncThunk<
  GetSalesStatisticResponse,
  GetSalesStatisticRequest,
  { rejectValue: string }
>("dashboard/getSalesStatistic", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Dashboard/GetSalesStatistic`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const rawData = await response.json();
    const data: GetSalesStatisticResponse = {
      ...rawData,
      salesStatistics: rawData.salesStatistics || rawData.SalesStatistics,
    };

    if (!data.success) {
      return rejectWithValue(data.message || "Failed to get sales statistics");
    }

    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});
export const getSalesCounter = createAsyncThunk<
  GetSalesCounterResponse,
  GetSalesCounterRequest,
  { rejectValue: string }
>("dashboard/getSalesCounter", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Dashboard/GetSalesCounter`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const rawData = await response.json();
    const data: GetSalesCounterResponse = {
      ...rawData,
      salesCounterJson: rawData.salesCounterJson || rawData.SalesCounterJson,
    };

    if (!data.success) {
      return rejectWithValue(data.message || "Failed to get sales counter");
    }

    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const getLeadSourceStat = createAsyncThunk<
  GetLeadSourceStatResponse,
  GetLeadSourceStatRequest,
  { rejectValue: string }
>("dashboard/getLeadSourceStat", async (requestData, { rejectWithValue }) => {
  try {
    const response = await fetch(`${API_BASE_URL}Dashboard/GetLeadSourceStat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const rawData = await response.json();
    const data: GetLeadSourceStatResponse = {
      ...rawData,
      leadSourceStats: rawData.leadSourceStats || rawData.LeadSourceStats,
    };

    if (!data.success) {
      return rejectWithValue(data.message || "Failed to get lead source stats");
    }

    return data;
  } catch (error) {
    return rejectWithValue(
      error instanceof Error ? error.message : "Unknown error",
    );
  }
});

export const getPhoneCallSourceStat = createAsyncThunk<
  GetPhoneCallSourceStatResponse,
  GetPhoneCallSourceStatRequest,
  { rejectValue: string }
>(
  "dashboard/getPhoneCallSourceStat",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}Dashboard/GetPhoneCallSourceStat`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const rawData = await response.json();
      const data: GetPhoneCallSourceStatResponse = {
        ...rawData,
        phoneCallSourceStats:
          rawData.phoneCallSourceStats || rawData.PhoneCallSourceStats || [],
      };

      if (!data.success) {
        return rejectWithValue(
          data.message || "Failed to get phone call source stats",
        );
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

export const getLeadsWithoutActivityPerDays = createAsyncThunk<
  LeadsWithoutActivityPerDaysResponse,
  LeadsWithoutActivityPerDaysRequest,
  { rejectValue: string }
>(
  "dashboard/getLeadsWithoutActivityPerDays",
  async (requestData, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}Dashboard/GetLeadsWithoutActivityPerDays`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestData),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const rawData = await response.json();
      const data: LeadsWithoutActivityPerDaysResponse = {
        ...rawData,
        leadsWithoutActivityPerDays:
          rawData.leadsWithoutActivityPerDays ||
          rawData.LeadsWithoutActivityPerDays,
      };

      if (!data.success) {
        return rejectWithValue(
          data.message || "Failed to get leads without activity per days",
        );
      }

      return data;
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);
